import { Divider, Icon, IconName, Text } from '@talos/kyoko';
import { Fragment } from 'react';
import styled from 'styled-components';
import {
  UnifiedLiquidityMarketTooltipStatus,
  type UnifiedLiquidityMarketTooltipGroup,
  type UnifiedLiquidityMarketTooltipStatus as UnifiedLiquidityMarketTooltipStatusType,
} from './unifiedLiquidityMarketTooltip';

export function UnifiedLiquidityMarketTooltipContent({
  groups,
  dataTestId,
  showStatus = true,
  statusTestIdPrefix,
}: {
  groups: UnifiedLiquidityMarketTooltipGroup[];
  dataTestId: string;
  showStatus?: boolean;
  statusTestIdPrefix: string;
}) {
  const visibleGroups = groups.filter(group => group.rows.length > 0);
  if (!visibleGroups.length) {
    return null;
  }

  return (
    <UnifiedLiquidityTooltipContent data-testid={dataTestId}>
      {visibleGroups.map((group, index) => (
        <Fragment key={group.key}>
          {index > 0 && <Divider data-testid="unified-liquidity-tooltip-market-separator" my="spacing4" />}
          <UnifiedLiquidityTooltipGroup>
            <UnifiedLiquidityTooltipHeader $showStatus={showStatus}>
              <TooltipHeaderText size="fontSizeSm" weight="fontWeightMedium">
                {group.displayName}
              </TooltipHeaderText>
              {showStatus && index === 0 ? (
                <TooltipHeaderText size="fontSizeSm" weight="fontWeightMedium">
                  UNIFIED LIQUIDITY
                </TooltipHeaderText>
              ) : null}
            </UnifiedLiquidityTooltipHeader>
            {group.rows.map(row => {
              return (
                <UnifiedLiquidityTooltipRow key={`${group.key}:${row.symbol}`} $showStatus={showStatus}>
                  <Text color="colorTextImportant" size="fontSizeMd" weight="fontWeightRegular">
                    {row.displayName}
                  </Text>
                  {showStatus && row.status ? (
                    <UnifiedLiquidityStatusPill
                      $status={row.status}
                      data-testid={`${statusTestIdPrefix}-${group.key}-${row.symbol}`}
                    >
                      <Icon icon={getUnifiedLiquidityTooltipRowStatusIcon(row.status)} size={12} />
                      {row.status}
                    </UnifiedLiquidityStatusPill>
                  ) : null}
                </UnifiedLiquidityTooltipRow>
              );
            })}
          </UnifiedLiquidityTooltipGroup>
        </Fragment>
      ))}
    </UnifiedLiquidityTooltipContent>
  );
}

function getUnifiedLiquidityTooltipRowStatusIcon(status: UnifiedLiquidityMarketTooltipStatusType) {
  switch (status) {
    case UnifiedLiquidityMarketTooltipStatus.Enabled:
      return IconName.Check;
    case UnifiedLiquidityMarketTooltipStatus.Unavailable:
      return IconName.ExclamationSolid;
    case UnifiedLiquidityMarketTooltipStatus.Disabled:
      return IconName.Close;
  }
}

const UnifiedLiquidityTooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 270px;
`;
const UnifiedLiquidityTooltipGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing4}px;
`;
const UnifiedLiquidityTooltipHeader = styled.div<{ $showStatus: boolean }>`
  display: grid;
  grid-template-columns: ${({ $showStatus }) => ($showStatus ? 'minmax(110px, 1fr) auto' : '1fr')};
  gap: ${({ theme }) => theme.spacing16}px;
  align-items: center;
`;
const TooltipHeaderText = styled(Text)`
  color: ${({ theme }) => theme.colorTextSubtle};
  text-transform: uppercase;
`;
const UnifiedLiquidityTooltipRow = styled.div<{ $showStatus: boolean }>`
  display: grid;
  grid-template-columns: ${({ $showStatus }) => ($showStatus ? 'minmax(110px, 1fr) auto' : '1fr')};
  gap: ${({ theme }) => theme.spacing16}px;
  align-items: center;
`;
const UnifiedLiquidityStatusPill = styled.span<{ $status: UnifiedLiquidityMarketTooltipStatusType }>`
  align-items: center;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case UnifiedLiquidityMarketTooltipStatus.Enabled:
        return theme.colors.blue.default;
      case UnifiedLiquidityMarketTooltipStatus.Unavailable:
        return theme.backgroundWarningButton;
      case UnifiedLiquidityMarketTooltipStatus.Disabled:
        return theme.colors.gray['070'];
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  color: ${({ $status, theme }) =>
    $status === UnifiedLiquidityMarketTooltipStatus.Unavailable
      ? theme.colorTextWarningButton
      : theme.colors.white.default};
  display: inline-flex;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  gap: ${({ theme }) => theme.spacing4}px;
  justify-content: center;
  justify-self: end;
  min-width: 72px;
  padding: ${({ theme }) => `${theme.spacing2}px ${theme.spacing6}px`};
`;
